import React, { useCallback, useEffect } from "react";
import {
  KDialog,
  KTextInput,
  KSelectAutocomplete,
} from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import { getTransformations } from "../../apiCalls/config/transformations";
import { getTemplates } from "../../apiCalls/config/templates";
import { getFullTemplate } from "../../apiCalls/config/fullTemplates";

interface Props {
  open: boolean;
  edit: string;
  handleClose: (reload?: boolean) => void;
  onSave: (
    fullTemplateName: string,
    description: string,
    templateName: string,
    transformationName: string,
    edit?: string,
  ) => void;
}

const FullTemplateView: React.FC<Props> = ({
  open,
  edit,
  handleClose,
  onSave,
}: Props) => {
  const [
    {
      fullTemplateName,
      description,
      templates,
      templateName,
      transformations,
      transformationName,
      nameError,
      nameErrorText,
    },
    setState,
  ] = useSetState({
    fullTemplateName: "",
    description: "",
    templates: [],
    templateName: "",
    transformations: [],
    transformationName: "",
    nameError: false,
    nameErrorText: "",
  });

  const handleChange = (e: any) => {
    setState({ [e.target.name]: e.target.value });
  };

  const handleSelectChange = (
    name: string,
    value: { label: string; value: string },
  ) => {
    setState({ [name]: value.value });
  };

  useEffect(() => {
    if (open) {
      setState({
        fullTemplateName: "",
        description: "",
        templateName: "",
        transformationName: "",
      });
      getTemplates("", 1, null).then((res: any) =>
        setState({
          templates: res?.data?.map((x) => ({
            label: x.name,
            value: x.name,
          })),
        }),
      );
      getTransformations("", 1, null).then((res: any) =>
        setState({
          transformations: res?.data?.map((x) => ({
            label: x.name,
            value: x.name,
          })),
        }),
      );
      if (edit) {
        getFullTemplate(edit).then((res) => {
          const { data } = res;
          setState({
            fullTemplateName: data?.name,
            description: data?.description,
            templateName: data?.template_name,
            transformationName: data?.transformation_name,
          });
        });
      }
    }
  }, [open]);

  const handleSave = useCallback(() => {
    onSave(
      fullTemplateName,
      description,
      templateName,
      transformationName,
      edit,
    );
    handleClose(true);
  }, [fullTemplateName, description, templateName, transformationName, edit]);

  useEffect(() => {
    if (fullTemplateName.includes(" ")) {
      setState({
        nameError: true,
        nameErrorText: "No spaces allowed in the name",
      });
    } else {
      setState({ nameError: false, nameErrorText: "" });
    }
  }, [fullTemplateName]);

  return (
    <KDialog
      open={open}
      title={edit ? "Edit Full Template" : "New Full Template"}
      size="xs"
      onClose={() => handleClose()}
      primaryCTAonClick={handleSave}
      primaryCTAText={edit ? "Update" : "Create"}
      primaryCTAColor="primary"
      secondaryCTAonClick={() => handleClose()}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={!fullTemplateName || !templateName || !transformationName}
    >
      <KTextInput
        onChange={handleChange}
        name="fullTemplateName"
        value={fullTemplateName}
        label="Full Template Name"
        placeholder="XYZ"
        width="100%"
        required
        error={nameError}
        errorText={nameErrorText}
        helperText="No Spaces allowed"
      />
      <KTextInput
        onChange={handleChange}
        name="description"
        value={description}
        label="Description"
        placeholder="XYZ"
        width="100%"
      />
      <KSelectAutocomplete
        required
        placeholder="Choose Template..."
        name="templateName"
        elements={templates}
        onChange={handleSelectChange}
        value={templateName}
        label="Template"
        style={{ width: "100%" }}
      />
      <KSelectAutocomplete
        required
        placeholder="Choose Transformation..."
        name="transformationName"
        elements={transformations}
        onChange={handleSelectChange}
        value={transformationName}
        label="Transformation"
        style={{ width: "100%" }}
      />
    </KDialog>
  );
};

export default FullTemplateView;
